import { Injectable, signal, computed } from '@angular/core';
import { SoldTicketsApiService } from './sold-tickets-api.service';
import { SoldTicket, SoldTicketResponse } from 'src/app/models/core/sold-ticket.model';
import { GetAllTicketsParams, RecentTicket, RecentTicketsResponse } from 'src/app/models/core/recent-ticket.model';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SoldTicketsService {
  // === Tickets ===
  readonly ticketsEcommerce = signal<RecentTicket[]>([]);
  readonly ticketsLink = signal<RecentTicket[]>([]);

  // === Pagination ===
  readonly ecommercePage = signal<number>(1);
  readonly linksPage = signal<number>(1);
  readonly ecommerceTotalPages = signal<number>(1);
  readonly linksTotalPages = signal<number>(1);

  // === Totals ===
  readonly totalTicketsEcommerce = signal<number>(0);
  readonly totalTicketsLinks = signal<number>(0);

  // === Loading states ===
  readonly loadingMore = signal(false);
  readonly fullLoading = signal<boolean>(false);
  readonly partialLoading = signal<boolean>(false);

  // === Filters and Search ===
  readonly filters = signal<GetAllTicketsParams>({});
  readonly searchQuery = signal<string>('');
  readonly perPage = signal<number>(10);

  readonly hasDirectSales = computed(() => this.totalTicketsLinks() > 0);
  readonly hasEcommerceSales = computed(() => this.totalTicketsEcommerce() > 0);
  readonly hasAnySales = computed(() => this.hasDirectSales() || this.hasEcommerceSales());

  constructor(private readonly api: SoldTicketsApiService) { }


  loadInitialTickets(type: 'full' | 'partial' = 'full') {
    this.fullLoading.set(type === 'full');
    this.partialLoading.set(type === 'partial');

    const ecommerceParams: GetAllTicketsParams = {
      ...this.filters(),
      payment_type: 'ecommerce',
      per_page: this.perPage()
    };

    const linkParams: GetAllTicketsParams = {
      ...this.filters(),
      payment_type: 'direct',
      per_page: this.perPage()
    };

    const ecommerceRequest$ = this.api.getAllTickets(ecommerceParams);
    const linkRequest$ = this.api.getAllTickets(linkParams);

    forkJoin([ecommerceRequest$, linkRequest$]).pipe(
      finalize(() => {
        this.fullLoading.set(false);
        this.partialLoading.set(false);
      })
    ).subscribe({
      next: ([ecommerceResponse, linkResponse]) => {
        // Process ecommerce response
        this.ticketsEcommerce.set(ecommerceResponse.data);
        this.ecommercePage.set(1);
        this.ecommerceTotalPages.set(Math.ceil(ecommerceResponse.meta.total / this.perPage()));
        this.totalTicketsEcommerce.set(ecommerceResponse.meta.total);

        // Process direct sale response
        this.ticketsLink.set(linkResponse.data);
        this.linksPage.set(1);
        this.linksTotalPages.set(Math.ceil(linkResponse.meta.total / this.perPage()));
        this.totalTicketsLinks.set(linkResponse.meta.total);
      },
      error: (error) => {
        console.error('Error cargando tickets vendidos:', error);
        this.ticketsEcommerce.set([]);
        this.ticketsLink.set([]);
        this.totalTicketsEcommerce.set(0);
        this.totalTicketsLinks.set(0);
      }
    });
  }

  /**
   * Load more tickets (pagination)
   * @param type Type of tickets to load (ecommerce or direct)
   */
  loadMoreTickets(type: 'ecommerce' | 'direct') {
    this.loadingMore.set(true);

    if (type === 'ecommerce') {
      const nextPage = this.ecommercePage() + 1;
      if (nextPage <= this.ecommerceTotalPages()) {
        const params: GetAllTicketsParams = {
          ...this.filters(),
          payment_type: 'ecommerce',
          per_page: this.perPage()
        };

        this.api.getAllTickets(params).pipe(
          finalize(() => this.loadingMore.set(false))
        ).subscribe({
          next: (response: RecentTicketsResponse) => {
            const currentTickets = this.ticketsEcommerce();
            this.ticketsEcommerce.set([...currentTickets, ...response.data]);
            this.ecommercePage.set(nextPage);
          },
          error: (error) => {
            console.error('Error cargando más tickets ecommerce:', error);
          }
        });
      }
    } else {
      const nextPage = this.linksPage() + 1;
      if (nextPage <= this.linksTotalPages()) {
        const params: GetAllTicketsParams = {
          ...this.filters(),
          payment_type: 'direct',
          per_page: this.perPage()
        };

        this.api.getAllTickets(params).pipe(
          finalize(() => this.loadingMore.set(false))
        ).subscribe({
          next: (response: RecentTicketsResponse) => {
            const currentTickets = this.ticketsLink();
            this.ticketsLink.set([...currentTickets, ...response.data]);
            this.linksPage.set(nextPage);
          },
          error: (error) => {
            console.error('Error cargando más tickets venta directa:', error);
          }
        });
      }
    }
  }

  /**
   * Update filters and reload tickets
   * @param filters New filters
   */
  updateFilters(filters: Partial<GetAllTicketsParams>) {
    this.filters.update(current => ({ ...current, ...filters }));
    this.ecommercePage.set(1);
    this.linksPage.set(1);
  }

  /**
   * Update search query
   * @param query New query
   */
  updateSearchQuery(query: string) {
    this.searchQuery.set(query);
    this.filters.update(current => ({ ...current, reference: query }));
    this.ecommercePage.set(1);
    this.linksPage.set(1);
  }

  /**
   * Clear all filters and searches
   */
  clearFilters() {
    this.filters.set({});
    this.searchQuery.set('');
    this.ecommercePage.set(1);
    this.linksPage.set(1);
  }


  /**
   * Update number of elements per page
   * @param perPage Elements per page
   */
  updatePerPage(perPage: number) {
    this.perPage.set(perPage);
    this.ecommercePage.set(1);
    this.linksPage.set(1);
  }

  /**
   * Refresh current data
   */
  refresh() {
    this.loadInitialTickets('partial');
  }

  /**
   * Reset Initial state
   */
  reset() {
    this.ticketsEcommerce.set([]);
    this.ticketsLink.set([]);
    this.ecommercePage.set(1);
    this.linksPage.set(1);
    this.ecommerceTotalPages.set(1);
    this.linksTotalPages.set(1);
    this.totalTicketsLinks.set(0);
    this.totalTicketsEcommerce.set(0);
    this.filters.set({});
    this.searchQuery.set('');
    this.loadingMore.set(false);
    this.fullLoading.set(false);
    this.partialLoading.set(false);
  }
}
